import { AUDIENCE_CARD_TYPE, AudienceCardProps } from '@/types/registration.types';
import Table from '@layouts/layout-table/layout-table';
import TableBody from '@layouts/layout-table/layout-table-body';
import TableData from '@layouts/layout-table/layout-table-data';
import TableHead from '@layouts/layout-table/layout-table-head';
import TableRow from '@layouts/layout-table/layout-table-row';
import SortButton from '@ui/buttons/sort-button/sort-button';
import Checkbox from '@ui/checkbox/checkbox';
import Container from '@ui/container/container';
import { ICONS } from '@ui/icons/types';
import { AllAudienceListsSort } from '../types';
import { SortDirection } from '@ui/buttons/sort-button/types';
import { useEffect, useState } from 'react';
import Icon from '@ui/icons/icon';
import Text from '@ui/text/text/text';
import OptionalComponent from '@ui/optional-component/optional-component';
import AudienceActions from './new-audiences/audience-actions';
import useFetchProjectNamesForList from '@/connection/hooks/use-fetch-project-names-for-list';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import TruncatedTextList from '@ui/truncated-list/truncated-text-list';

type AudiencesListViewProps = {
  isLoading: boolean;
  filteredAudienceCardProps: AudienceCardProps[];
  selectedPasscodeListIds: number[];
  selectedRegistrantLists: AUDIENCE_CARD_TYPE[];
  onCheckboxChange: (checked: boolean, audienceCardType: AUDIENCE_CARD_TYPE, passcodeListId?: number) => void;
  onSelectAll: (select: boolean, cards: AudienceCardProps[]) => void;
};

const getSortState = (sortBy: AllAudienceListsSort['sortBy'], sort: AllAudienceListsSort | undefined) => {
  if (sort?.sortBy === sortBy) {
    return sort.sortOrder === 'asc' ? SortDirection.up : SortDirection.down;
  }

  return SortDirection.none;
};

const AudiencesListView = ({
  isLoading,
  filteredAudienceCardProps,
  selectedPasscodeListIds,
  selectedRegistrantLists,
  onCheckboxChange,
  onSelectAll,
}: AudiencesListViewProps) => {
  const [allAudienceListsSort, setAllAudienceListsSort] = useState<AllAudienceListsSort | undefined>();
  const [sortedAudienceLists, setSortedAudienceLists] = useState<AudienceCardProps[]>(filteredAudienceCardProps);

  const passcodeListUsage = useFetchProjectNamesForList();

  useEffect(() => {
    if (!allAudienceListsSort) {
      setSortedAudienceLists(filteredAudienceCardProps);
    } else {
      setSortedAudienceLists(applySortToList(filteredAudienceCardProps, allAudienceListsSort));
    }
  }, [filteredAudienceCardProps, allAudienceListsSort]);

  const applySortToList = (list: AudienceCardProps[], sort: AllAudienceListsSort): AudienceCardProps[] => {
    const dir = sort.sortOrder === 'asc' ? 1 : -1;
    return [...list].toSorted((a, b) => {
      switch (sort.sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '') * dir;
        case 'member_count':
          return (Number(a.member_count ?? 0) - Number(b.member_count ?? 0)) * dir;
        case 'created_date':
          return (new Date(a.created_date ?? 0).getTime() - new Date(b.created_date ?? 0).getTime()) * dir;
        default:
          return 0;
      }
    });
  };

  const clickSort = (sortBy: AllAudienceListsSort['sortBy']) => () => {
    let newSort: AllAudienceListsSort | undefined;

    if (!allAudienceListsSort || allAudienceListsSort.sortBy !== sortBy) {
      newSort = { sortBy, sortOrder: 'asc' };
    } else if (allAudienceListsSort.sortOrder === 'asc') {
      newSort = { sortBy, sortOrder: 'desc' };
    } else {
      newSort = undefined;
    }

    setAllAudienceListsSort(newSort);
    setSortedAudienceLists(newSort ? applySortToList(filteredAudienceCardProps, newSort) : filteredAudienceCardProps);
  };

  const isCardSelected = (card: AudienceCardProps) =>
    card.audienceCardType === AUDIENCE_CARD_TYPE.PASSCODE_LIST
      ? card.passcodeListId !== undefined && selectedPasscodeListIds.includes(card.passcodeListId)
      : selectedRegistrantLists.includes(card.audienceCardType);

  const isAllSelected = sortedAudienceLists.length > 0 && sortedAudienceLists.every(isCardSelected);

  const handleSelectAll = () => {
    onSelectAll(!isAllSelected, sortedAudienceLists);
  };

  return (
    <OptionalComponent display={!isLoading}>
      <Table>
        <TableHead
          className="passcode-list-member-table-header"
          style={{ borderTop: 'none', position: 'sticky', top: 0, zIndex: 2 }}
        >
          <TableRow>
            <th>
              <Checkbox
                checked={isAllSelected}
                onChange={handleSelectAll}
                value={isAllSelected ? 'all' : ''}
                selectedIconName={ICONS.ROUND_CHECKBOX_SELECTED}
                unselectedIconName={ICONS.ROUND_CHECKBOX_UNSELECTED}
              />
            </th>
            <th>
              <div className="flex ai-center">
                <span>NAME</span>{' '}
                <SortButton onClick={clickSort('name')} sortDirection={getSortState('name', allAudienceListsSort)} />
              </div>
            </th>
            <th>
              <div className="flex ai-center">
                <span>PROFILES</span>{' '}
                <SortButton
                  onClick={clickSort('member_count')}
                  sortDirection={getSortState('member_count', allAudienceListsSort)}
                />
              </div>
            </th>
            <th>
              <div className="flex ai-center">
                <span>DATE CREATED</span>{' '}
                <SortButton
                  onClick={clickSort('created_date')}
                  sortDirection={getSortState('created_date', allAudienceListsSort)}
                />
              </div>
            </th>
            <th>
              <div className="flex ai-center">
                <span>USED IN</span>{' '}
                {/* 
                    no real way to sort by used in, but it is in the mock so stubbing this out for now 
                    does not do anything, but it matches the mock
                */}
                <SortButton
                  onClick={clickSort('used_in')}
                  sortDirection={getSortState('used_in', allAudienceListsSort)}
                />
              </div>
            </th>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedAudienceLists.map((audienceCardProps) => {
            return (
              <TableRow key={audienceCardProps.passcodeListId || audienceCardProps.audienceCardType}>
                <TableData width={32}>
                  <Checkbox
                    checked={isCardSelected(audienceCardProps)}
                    onChange={() =>
                      onCheckboxChange(
                        !isCardSelected(audienceCardProps),
                        audienceCardProps.audienceCardType,
                        audienceCardProps.passcodeListId
                      )
                    }
                    value={audienceCardProps.passcodeListId || audienceCardProps.audienceCardType}
                    selectedIconName={ICONS.ROUND_CHECKBOX_SELECTED}
                    unselectedIconName={ICONS.ROUND_CHECKBOX_UNSELECTED}
                  />
                </TableData>
                <TableData width={223}>
                  <Container className="flex fd-row gap-4">
                    <Text className="P2">{audienceCardProps.name}</Text>
                    {audienceCardProps.passcodeList?.cloned_source && audienceCardProps.passcodeList?.description && (
                      <Text className="C1" color="var(--global-ico-text-2)">
                        {' '}
                        - {audienceCardProps.passcodeList.description}
                      </Text>
                    )}
                  </Container>
                </TableData>
                <TableData width={121}>
                  <Container className="flex ai-center gap-6">
                    <Icon name={ICONS.USERS_AUDIENCE} size={12} color="var(--global-ico-text-2)" />
                    <Text className="C1">{audienceCardProps.member_count}</Text>
                  </Container>
                </TableData>
                <TableData width={157}>
                  {audienceCardProps.created_date
                    ? new Date(audienceCardProps.created_date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : 'N/A'}
                </TableData>
                <TableData width={299}>
                  <OptionalComponent
                    display={
                      audienceCardProps.audienceCardType === AUDIENCE_CARD_TYPE.PASSCODE_LIST &&
                      !!audienceCardProps.passcodeListId
                    }
                  >
                    <TooltipPortal
                      tooltipContent={
                        <>
                          {passcodeListUsage?.[audienceCardProps.passcodeListId!]?.map((event) => (
                            <Text color="var(--gray-true-dark-100)">{event}</Text>
                          ))}
                        </>
                      }
                    >
                      <Container className="" width="299px">
                        <TruncatedTextList
                          key={audienceCardProps.passcodeListId}
                          items={(passcodeListUsage?.[audienceCardProps.passcodeListId!] ?? []).map((event) => {
                            return { label: event, value: event, meta: event };
                          })}
                          plusMoreWidth={20}
                          width={299}
                          height={24}
                          gap={0}
                          containerStyle={{
                            pointerEvents: 'none',
                            cursor: 'pointer',
                            opacity: 1,
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 1,
                          }}
                          placeholderStyles={{ width: '160px' }}
                        />
                      </Container>
                    </TooltipPortal>
                  </OptionalComponent>
                </TableData>
                <TableData width={32}>
                  <AudienceActions
                    audienceCardType={audienceCardProps.audienceCardType}
                    passcodeListId={audienceCardProps.passcodeListId}
                    passcodeList={audienceCardProps.passcodeList}
                    name={audienceCardProps.name}
                    isListView
                  />
                </TableData>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </OptionalComponent>
  );
};

export default AudiencesListView;
